import type { MutableRefObject } from "react";
import {
  confirmFastTranslateUploadApi,
  getFastTranslatePresignedUrlsApi,
} from "../../service/fastTranslateSeries";
import { uploadFileToTosApi } from "../../service/myWork";
import { getVideoMetadata } from "../../utils/videoMetadata";
import { processVideoUrl } from "../../utils/videoUrl";

export type FastTranslateWorkspaceUploadResult = {
  coverUrl: string;
  episodeName: string;
  duration: number;
};

/**
 * 与 QuickTransCreateSeriesModal 同一套预签名 → TOS → confirm 流程，
 * 供精品上传页在一键速译剧集下追加单集视频。
 */
export const uploadFastTranslateWorkspaceVideo = async (
  uploadBatchNoRef: MutableRefObject<string | undefined>,
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal
): Promise<FastTranslateWorkspaceUploadResult> => {
  const meta = await getVideoMetadata(file);
  const coverFile = new File([meta.coverBlob], meta.coverFileName, {
    type: meta.coverBlob.type || "image/jpeg",
  });

  const presigned = await getFastTranslatePresignedUrlsApi({
    uploadBatchNo: uploadBatchNoRef.current,
    files: [{ fileName: file.name, coverFileName: meta.coverFileName }],
  });

  if (presigned.code !== 0 || !presigned.data?.files?.length) {
    throw new Error(presigned.msg || "获取预签名失败");
  }

  const batchNo = presigned.data.uploadBatchNo;
  if (batchNo) {
    uploadBatchNoRef.current = batchNo;
  }

  const sign =
    presigned.data.files.find((f) => f.sourceFileName === file.name) ?? presigned.data.files[0];
  if (!sign || !uploadBatchNoRef.current) {
    throw new Error("预签名数据不完整");
  }

  await uploadFileToTosApi(file, sign.sourceSignature, sign.sourcePath, (p) => {
    onProgress?.(Math.min(90, Math.round(p * 0.9)));
  }, signal);
  await uploadFileToTosApi(
    coverFile,
    sign.coverSignature,
    sign.coverPath,
    onProgress
      ? (p) => {
          onProgress(90 + Math.round((p / 100) * 2));
        }
      : undefined,
    signal
  );
  onProgress?.(92);
  onProgress?.(94);

  const confirmResp = await confirmFastTranslateUploadApi({
    uploadBatchNo: uploadBatchNoRef.current,
    files: [
      {
        fileName: file.name,
        filePath: sign.sourcePath,
        coverFileName: meta.coverFileName,
        coverFilePath: sign.coverPath,
        duration: meta.duration,
      },
    ],
  });

  if (confirmResp.code !== 0) {
    throw new Error(confirmResp.msg || "确认上传失败");
  }

  const row =
    confirmResp.data?.files?.find((f) => f.sourceFileName === file.name) ?? confirmResp.data?.files?.[0];
  if (!row) {
    throw new Error("确认上传无返回数据");
  }
  onProgress?.(100);
  return {
    coverUrl: processVideoUrl(row.coverUrl),
    episodeName: row.episodeName,
    duration: Number(row.duration) || 0,
  };
};
